"use client";

import Script from "next/script";
import { useCallback, useEffect, useRef } from "react";
import {
  TRUSTPILOT_BUSINESS_UNIT_ID,
  TRUSTPILOT_TEMPLATE_ID,
  TRUSTPILOT_REVIEW_URL,
  TRUSTPILOT_LOCALE,
  hasTrustpilot,
} from "@/lib/marketing/trustpilot";

/**
 * G3-5 — live Trustpilot TrustBox embed.
 *
 * Renders the official TrustBox (real reviews, hydrated by Trustpilot's
 * bootstrap script) when a business-unit ID is configured, and NOTHING
 * otherwise — never a fabricated or empty-state widget. See lib/marketing/
 * trustpilot.ts for how to switch it on.
 *
 * Note: when enabled this loads Trustpilot's third-party widget script. It is
 * review content (not analytics tracking) so it is not consent-gated here; if
 * Trustpilot is ever found to set non-essential cookies, gate it like
 * components/analytics/microsoft-clarity.tsx.
 */

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (el: HTMLElement | null, forceReload?: boolean) => void;
    };
  }
}

interface TrustpilotWidgetProps {
  /** Override the configured business-unit ID (mainly for tests). */
  businessUnitId?: string;
  /** Override the TrustBox template (widget style). */
  templateId?: string;
  theme?: "light" | "dark";
  /** TrustBox iframe height, e.g. "52px". */
  height?: string;
  width?: string;
  className?: string;
}

export function TrustpilotWidget({
  businessUnitId = TRUSTPILOT_BUSINESS_UNIT_ID,
  templateId = TRUSTPILOT_TEMPLATE_ID,
  theme = "light",
  height = "52px",
  width = "100%",
  className,
}: TrustpilotWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);

  const hydrate = useCallback(() => {
    // Trustpilot's bootstrap script turns the placeholder <div> into the live
    // embed. If it has already loaded (another widget mounted it), hydrate now;
    // otherwise <Script onReady> calls this once it's available.
    if (typeof window !== "undefined" && window.Trustpilot && ref.current) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // No business-unit ID configured → render nothing.
  if (!hasTrustpilot(businessUnitId)) return null;

  return (
    <>
      <Script
        src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        strategy="lazyOnload"
        onReady={hydrate}
      />
      {/* TrustBox placeholder — hydrated into a live real-reviews embed. The
          inner <a> is the no-JS fallback to the public review page. */}
      <div
        ref={ref}
        className={`trustpilot-widget ${className ?? ""}`.trim()}
        data-locale={TRUSTPILOT_LOCALE}
        data-template-id={templateId}
        data-businessunit-id={businessUnitId}
        data-style-height={height}
        data-style-width={width}
        data-theme={theme}
      >
        <a href={TRUSTPILOT_REVIEW_URL} target="_blank" rel="noopener noreferrer">
          Trustpilot
        </a>
      </div>
    </>
  );
}
